import { Flex, Spacer, useToast } from "@chakra-ui/react";
import { isEqual } from "lodash";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { TenantRead } from "@nens/rana/api-v1";
import { ButtonWrapper, RanaButton } from "@nens/ranalib";

import { AdminForm } from "@/features/admin";

import { useCreateTenant } from "./api/create-tenant";
import { useEditTenant } from "./api/edit-tenant";
import { AdminDeleteTenantModal } from "./components/admin-delete-tenant-modal";
import { useTenantCreateOrEditFormSchema } from "./hooks/use-tenant-admin-create-or-edit-form-schema";

export type TenantCreateOrEditProps =
  | {
      type: "creating";
      close: () => void;
    }
  | {
      type: "editing";
      tenant: TenantRead;
    };

export function AdminTenantCreateOrEdit(props: TenantCreateOrEditProps) {
  const { schema, uiSchema } = useTenantCreateOrEditFormSchema(props);
  const intl = useIntl();
  const toast = useToast();
  const { mutate: createMutate } = useCreateTenant();
  const { mutate: editMutate } = useEditTenant();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  return (
    <>
      {deleteModalOpen && props.type === "editing" && (
        <AdminDeleteTenantModal
          onClose={() => setDeleteModalOpen(false)}
          tenants={[props.tenant]}
        />
      )}
      <AdminForm
        schema={schema}
        formData={props.type === "editing" ? props.tenant : undefined}
        uiSchema={uiSchema}
        onSubmit={({ formData }) => {
          if (props.type === "creating") {
            createMutate(formData, {
              onSuccess: () => {
                window.location.href = `/${formData.id}/admin/detail`;
                toast({
                  title: intl.formatMessage({ id: "success", defaultMessage: "Success!" }),
                  description: "Successfully created tenant",
                });
              },
            });
            return;
          }

          if (isEqual(formData, props.tenant)) {
            toast({
              status: "error",
              title: "No changes made.",
              description: "No changes were detected to save.",
            });
            return;
          }

          editMutate(formData, {
            onSuccess: () => {
              toast({
                title: intl.formatMessage({ id: "success", defaultMessage: "Success!" }),
                description: "Successfully updated organisation",
              });
            },
          });
        }}
      >
        <Flex mt={11} justifyContent="space-between">
          {props.type === "editing" && (
            <RanaButton onClick={() => setDeleteModalOpen(true)} variant="danger">
              <FormattedMessage
                id="button.delete_organisation"
                defaultMessage="Delete organisation"
              />
            </RanaButton>
          )}
          <Spacer />
          <ButtonWrapper>
            <RanaButton ml="auto" type="submit" variant="primary">
              {props.type === "creating" ? (
                <FormattedMessage
                  id="button.create_organisation"
                  defaultMessage="Create organisation"
                />
              ) : (
                <FormattedMessage id="button.save_changes" defaultMessage="Save Changes" />
              )}
            </RanaButton>
          </ButtonWrapper>
        </Flex>
      </AdminForm>
    </>
  );
}
